import { useEffect, type MouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../../../utils/cx';
import styles from './Modal.module.scss';
import React from 'react';
import { IconClose } from '../../../assets/icons/icons';

interface ModalProps {
    open: boolean;
    title?: string;
    description?: ReactNode;
    icon?: ReactNode;
    tone?: 'accent' | 'danger';
    width?: number;
    headerExtra?: ReactNode;
    children?: ReactNode;
    footer?: ReactNode;
    onClose: () => void;
    closeOnOverlayClick?: boolean;
}

export function Modal({ open, title, description, icon, tone = 'accent', width = 520, headerExtra, children, footer, closeOnOverlayClick = true, onClose }: ModalProps) {
    const hasHead = Boolean(title || description || icon || headerExtra);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    useEffect(() => {
        if (!open) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previous;
        };
    }, [open]);

    if (!open) return null;

    const onOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) onClose();
    };

    return createPortal(
        <div className={styles.overlay}
            onMouseDown={(event) => {
                if (closeOnOverlayClick && event.target === event.currentTarget) onClose();
            }}
        >
            <div className={styles.modal} style={{ width }} role="dialog" aria-modal="true" aria-label={title ?? 'Диалоговое окно'}>
                {hasHead ? (
                    <div className={styles.head}>
                        {icon && (
                            <div className={cx(styles.icon, tone === 'danger' && styles.iconDanger)}>{icon}</div>
                        )}
                        <div>
                            {title && <h3>{title}</h3>}
                            {description && <p>{description}</p>}
                        </div>
                        {headerExtra}
                        <button type="button" className={styles.close} onClick={onClose} aria-label="Закрыть окно">
                            <IconClose />
                        </button>
                    </div>
                ) : (
                    <button type="button" className={cx(styles.close, styles.closeFloating)} onClick={onClose} aria-label="Закрыть окно">
                        <IconClose />
                    </button>
                )}

                {children && <div className={styles.body}>{children}</div>}
                {footer && <div className={styles.foot}>{footer}</div>}
            </div>
        </div>,
        document.body,
    );
}

export const ModalActions = ({ children }: { children: ReactNode }) => (
    <div className={styles.footRight}>{children}</div>
);

export const ModalNote = ({ children }: { children: ReactNode }) => (
    <span className={styles.footNote}>{children}</span>
);